export const ASSET_KINDS = ["image", "video", "audio", "svg"] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

export const ASSET_KIND_LABELS: Record<AssetKind, string> = {
  image: "Bilder",
  video: "Videos",
  audio: "Audio",
  svg: "Grafiken",
};

/** Allowed upload types. The backend verifies the real type via magic bytes. */
export const ALLOWED_MIME_TYPES: Record<string, AssetKind> = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "image/gif": "image",
  "image/avif": "image",
  "image/heic": "image",
  "image/heif": "image",
  "image/svg+xml": "svg",
  "video/mp4": "video",
  "video/quicktime": "video",
  "video/webm": "video",
  "video/x-matroska": "video",
  "audio/mpeg": "audio",
  "audio/mp4": "audio",
  "audio/x-m4a": "audio",
  "audio/wav": "audio",
  "audio/x-wav": "audio",
  "audio/ogg": "audio",
  "audio/aac": "audio",
  "audio/flac": "audio",
};

export function assetKindForMime(mime: string | null | undefined): AssetKind | null {
  if (!mime) return null;
  return ALLOWED_MIME_TYPES[mime.toLowerCase()] ?? null;
}

export const UPLOAD_ACCEPT = Object.keys(ALLOWED_MIME_TYPES).join(",");

/**
 * Background removal ("Freistellen"): the result is a new PNG next to the
 * original, whose longer side is at most this long (print at A3 / 300 dpi).
 */
export const CUTOUT_MAX_SIDE = 6000;
/** Larger originals are not cut out (decoding alone would need too much memory). */
export const CUTOUT_MAX_INPUT_PIXELS = 100_000_000;

/** Suffix of the cut-out copy's name, e.g. "Portrait (freigestellt)". */
export const CUTOUT_NAME_SUFFIX = " (freigestellt)";

export function cutoutName(name: string): string {
  return `${name.slice(0, 255 - CUTOUT_NAME_SUFFIX.length)}${CUTOUT_NAME_SUFFIX}`;
}

/** Why an asset cannot be cut out, or null when it can. */
export function cutoutBlocker(asset: {
  kind: AssetKind;
  status: string;
  width: number | null;
  height: number | null;
  cutout?: { sourceId: string } | null;
}): string | null {
  if (asset.kind !== "image") return "Nur Fotos und Bilder lassen sich freistellen.";
  if (asset.cutout) return "Das Bild ist bereits freigestellt.";
  if (asset.status === "processing") return "Das Bild wird noch verarbeitet.";
  if (asset.status !== "ready" || !asset.width || !asset.height) {
    return "Das Bild konnte nicht gelesen werden.";
  }
  if (asset.width * asset.height > CUTOUT_MAX_INPUT_PIXELS) {
    return "Das Bild ist zu groß zum Freistellen (höchstens 100 Megapixel).";
  }
  return null;
}

/**
 * Storage categories used in usage reports. Asset-derived files (thumbnails,
 * previews) count towards the asset's category.
 */
export const STORAGE_CATEGORIES = [...ASSET_KINDS, "font", "document", "export", "other"] as const;
export type StorageCategory = (typeof STORAGE_CATEGORIES)[number];

export const STORAGE_CATEGORY_LABELS: Record<StorageCategory, string> = {
  ...ASSET_KIND_LABELS,
  font: "Schriften",
  document: "Dokumente",
  export: "Exporte",
  other: "Sonstiges",
};

const UNITS = ["B", "KB", "MB", "GB", "TB"];

export function formatBytes(bytes: number, fractionDigits = 1): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), UNITS.length - 1);
  const value = bytes / 1024 ** exponent;
  const digits = exponent === 0 ? 0 : fractionDigits;
  return `${value.toLocaleString("de-DE", { maximumFractionDigits: digits })} ${UNITS[exponent]}`;
}
